import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotificationContext } from "@/features/notifications/context/NotificationContext";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { formatDateTime } from "@/shared/utils/formatters";

type FilterType = "new" | "seen";

export const NotificationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifications, markAsRead, markMultipleAsRead } = useNotificationContext();
  const [filter, setFilter] = useState<FilterType>("new");
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filtered = notifications.filter((n) =>
    filter === "new" ? !n.is_read : n.is_read,
  );

  const handleFilterChange = (f: FilterType) => {
    setFilter(f);
    setIsSelecting(false);
    setSelectedIds(new Set());
  };

  const handleNotificationClick = (n: (typeof notifications)[number]) => {
    if (isSelecting) {
      toggleSelect(n.notification_id);
      return;
    }
    if (!n.is_read) markAsRead(n.notification_id);
    if (n.relation_type === "inquiries" && n.relation_id) {
      navigate("/inquiries", { state: { openTicketId: n.relation_id } });
    } else if (n.relation_type === "announcements" && n.relation_id) {
      navigate("/", { state: { focusId: n.relation_id } });
    } else if (n.relation_type === "return_home" && n.relation_id) {
      navigate("/return-home", { state: { openId: n.relation_id } });
    } else if (n.relation_type === "leave") {
      navigate("/leave/submissions", { state: { submissionId: n.relation_id } });
    } else if (n.relation_type === "task" && n.relation_id) {
      navigate("/tasks", { state: { openTaskId: n.relation_id } });
    } else if (n.relation_type === "app_support" && n.relation_id) {
      navigate("/support", { state: { openTicketId: Number(n.relation_id) } });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((n) => n.notification_id)));
    }
  };

  const handleMarkSeen = async () => {
    if (selectedIds.size === 0) return;
    await markMultipleAsRead(Array.from(selectedIds));
    setSelectedIds(new Set());
    setIsSelecting(false);
  };

  const handleCancelSelect = () => {
    setIsSelecting(false);
    setSelectedIds(new Set());
  };

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;

  return (
    <div>
      <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
        {t("notifications.title")}
      </h1>

      {/* Filter pills + Select button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {(["new", "seen"] as FilterType[]).map((f) => (
            <button
              key={f}
              onClick={() => handleFilterChange(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                filter === f
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
            >
              {f === "new" ? t("notifications.filter_new") : t("notifications.filter_seen")}
            </button>
          ))}
        </div>

        {filter === "new" && filtered.length > 0 && (
          <button
            onClick={isSelecting ? handleCancelSelect : () => setIsSelecting(true)}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            {isSelecting ? t("common.cancel") : t("common.select")}
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {isSelecting && filter === "new" && (
        <div className="flex items-center justify-between mb-3 px-1">
          <button
            onClick={handleSelectAll}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <span
              className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                allSelected ? "bg-indigo-600 border-indigo-600" : "border-gray-400"
              }`}
            >
              {allSelected && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                  <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            {t("common.select_all")}
          </button>

          <button
            onClick={handleMarkSeen}
            disabled={selectedIds.size === 0}
            className={`text-sm font-semibold px-3 py-1 rounded-full transition-colors ${
              selectedIds.size > 0
                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {t("notifications.mark_seen")}
            {selectedIds.size > 0 && ` (${selectedIds.size})`}
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState title={t("notifications.empty_state")} />
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => (
            <div
              key={n.notification_id}
              onClick={() => handleNotificationClick(n)}
              className={`p-3 md:p-4 rounded-lg border cursor-pointer transition-colors active:scale-[0.99] ${
                n.is_read ? "bg-white border-gray-200" : "bg-indigo-50 border-indigo-200"
              } ${isSelecting && selectedIds.has(n.notification_id) ? "ring-2 ring-indigo-400" : ""}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {isSelecting && (
                    <span
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                        selectedIds.has(n.notification_id)
                          ? "bg-indigo-600 border-indigo-600"
                          : "border-gray-400"
                      }`}
                    >
                      {selectedIds.has(n.notification_id) && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                          <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                  )}
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">{n.title}</h3>
                    <p className="text-sm text-gray-600 mt-0.5">{n.body}</p>
                  </div>
                </div>
                {!n.is_read && !isSelecting && (
                  <span className="w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0 mt-1.5" />
                )}
              </div>
              <p className={`text-xs text-gray-400 mt-2 ${isSelecting ? "ml-7" : ""}`}>
                {formatDateTime(n.created_at)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
